import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { handleError } from '@/services/handleError';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import type { TAuthRequest } from '@/types/api';

import { apiConnector } from '../integrations/api.connector';
import type { TAuthStore } from '../types/store';
import { createAsyncThunk } from './create-async-thunk';

export const forgotPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email: string): Promise<any> => {
    return await apiConnector.forgotePassword(email);
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, isRemember }: TAuthRequest): Promise<any> => {
    let ipData;
    try {
      const response = await axios.get(
        'https://api.ipdata.co?api-key=a8d0725d9ce39971566005bdbbf47348d9bfec23a17e49cf080f2ec4'
      );
      ipData = await response.data;
    } catch (error) {
      handleError(error, {
        notify: false
      });
    }

    ipData ??= { ip: '', latitude: '', longitude: '' };
    const { ip, latitude, longitude } = ipData;

    return await apiConnector.authJwt(
      email,
      password,
      isRemember,
      ip,
      String(latitude),
      String(longitude)
    );
  }
);

export const tokenValidate = createAsyncThunk('auth/validate', async () => {
  return await apiConnector.isValid();
});

const initialState: TAuthStore = {
  isAuthErrorModal: '',
  isLoading: false,
  isUserAuth: true,
  loginError: '',
  loginWarning: '',
  resetEmail: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setResetEmail: (state, action) => {
      state.resetEmail = action.payload;
    },
    resetLoginError: (state) => {
      state.loginError = '';
    },
    setLoginWarning: (state, action: PayloadAction<string>) => {
      state.loginWarning = action.payload;
    },
    setIsAuth: (state, action: PayloadAction<boolean>) => {
      state.isUserAuth = action.payload;
    },
    setIsAuthErrorModal: (
      state,
      action: PayloadAction<'finally' | 'reauthenticate' | ''>
    ) => {
      state.isAuthErrorModal = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.loginError = '';
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        const blockedMessage =
          action.error.message === 'User is not active' &&
          'Пользователь с данным email адресом был заблокирован.';
        const isAccountBlocked = action.error.message?.match(/@.*\./) !== null;

        state.loginError =
          blockedMessage ||
          (isAccountBlocked
            ? 'Превышено количество попыток!'
            : 'Неправильно указан email и/или пароль');
        state.loginWarning = (!blockedMessage && action.error.message) || '';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.status) {
          state.isUserAuth = true;
          state.loginWarning = '';
          state.loginError = '';
        } else {
          state.isUserAuth = false;
          state.loginError = 'Неправильно указан email и/или пароль';
        }
      })
      .addCase(tokenValidate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(tokenValidate.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(tokenValidate.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload === false) {
          state.isUserAuth = false;
          clearLocalStorageHelper();
        } else {
          state.isUserAuth = true;
        }
      })
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
      });
  }
});

export const {
  resetLoginError,
  setIsAuth,
  setIsAuthErrorModal,
  setLoginWarning,
  setResetEmail
} = authSlice.actions;
